import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/layout/Header";
import { Activity, Search, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Student } from "@/types";
import { MonitoringProfileDialog } from "@/components/nurse/special-monitoring/MonitoringProfileDialog";
import { MonitoringDashboard } from "@/components/nurse/special-monitoring/MonitoringDashboard";
import { ActiveProfilesList } from "@/components/nurse/special-monitoring/ActiveProfilesList";

export default function SpecialMonitoring() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [searchResults, setSearchResults] = useState<Student[]>([]);
  const [editingProfile, setEditingProfile] = useState<any>(null);

  const { data: currentTerm } = useQuery({
    queryKey: ['currentTerm'],
    queryFn: async () => {
      const { data: activeYear } = await supabase
        .from('academic_years')
        .select('id')
        .eq('is_active', true)
        .single();

      if (!activeYear) return null;

      const { data: term } = await supabase
        .from('terms')
        .select('*')
        .eq('is_active', true)
        .eq('academic_year_id', activeYear.id)
        .single();

      return term;
    }
  });

  // Search students
  const handleSearch = async (term: string) => {
    setSearchTerm(term);
    if (!term || !user?.schoolId) {
      setSearchResults([]);
      return;
    }

    const { data } = await supabase
      .from('students')
      .select('*')
      .eq('school_id', user.schoolId)
      .eq('is_archived', false)
      .or(`surname.ilike.%${term}%,other_names.ilike.%${term}%`)
      .limit(10);

    if (data) {
      setSearchResults(data.map(s => ({
        ...s,
        medical_alerts: (s.medical_alerts as any) || []
      })) as Student[]);
    }
  };

  const handleStudentSelect = (student: Student) => {
    setSelectedStudent(student);
    setSearchTerm("");
    setSearchResults([]);
  };

  const handleCreateProfile = () => {
    setEditingProfile(null);
    setShowProfileDialog(true);
  };

  const handleEditProfile = (profile: any, student: Student) => {
    setEditingProfile(profile);
    setSelectedStudent(student);
    setShowProfileDialog(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container px-4 py-6 mt-[147px]">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">Special Monitoring</h1>
            </div>
            {currentTerm && (
              <div className="text-sm text-muted-foreground">
                Active Term: <span className="font-semibold">{currentTerm.name}</span>
              </div>
            )}
          </div>

          {!selectedStudent ? (
            <div className="space-y-6">
              <Card className="p-6">
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search for a student to monitor..."
                      className="pl-10"
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </div>

                  {searchResults.length > 0 && (
                    <div className="border rounded-md divide-y">
                      {searchResults.map((student) => (
                        <button
                          key={student.id}
                          onClick={() => handleStudentSelect(student)}
                          className="w-full p-3 text-left hover:bg-accent transition-colors"
                        >
                          <div className="font-medium">
                            {student.surname}, {student.other_names}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {student.class} {student.stream}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {searchTerm && searchResults.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No students found matching "{searchTerm}"
                    </div>
                  )}
                </div>
              </Card>

              {user?.schoolId && currentTerm && (
                <ActiveProfilesList 
                  schoolId={user.schoolId}
                  currentTermId={currentTerm.id}
                  onViewProfile={setSelectedStudent}
                  onEditProfile={handleEditProfile}
                />
              )}
            </div>
          ) : (
            <MonitoringDashboard
              student={selectedStudent}
              onClose={() => setSelectedStudent(null)}
              onCreateProfile={handleCreateProfile}
            />
          )}
        </div>
      </main>

      {selectedStudent && currentTerm && (
        <MonitoringProfileDialog
          open={showProfileDialog}
          onOpenChange={setShowProfileDialog}
          student={selectedStudent}
          termId={currentTerm.id}
          existingProfile={editingProfile}
        />
      )}
    </div>
  );
}
